import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft2,
  ArrowRight2,
  Folder,
  Heart,
  Home2,
  Location,
  User,
} from "iconsax-react";

import { useAppTheme } from "../../app/providers/ThemeProvider";
import {
  getTabBarTotalHeight,
  LIGHT_TAB_COLORS,
  SIDE_ICON_SIZE,
} from "../../constants/tabBarLayout";
import { useAuth } from "../../hooks/auth";
import { getProfilePhotoSrc } from "../../utils/profilePhoto";

import {
  BottomRailGroup,
  CenterButton,
  CenterSlot,
  CollapseToggle,
  MainRailGroup,
  ProfileAvatarRing,
  ProfileTabButton,
  TabBarRoot,
  TabBarShadow,
  TabBarShell,
  TabButton,
  TabSlot,
} from "./styles";

type TabId = "home" | "favorites" | "repository" | "map" | "profile";

const MAIN_TABS: TabId[] = ["home", "favorites", "repository", "map"];

const TAB_PATHS: Record<TabId, string> = {
  home: "/dashboard",
  favorites: "/favorites",
  repository: "/repository",
  map: "/map",
  profile: "/profile",
};

const TAB_LABELS: Record<TabId, string> = {
  home: "Início",
  favorites: "Favoritos",
  repository: "Repositório pessoal",
  map: "Mapa",
  profile: "Perfil",
};

function isTabActive(pathname: string, tab: TabId): boolean {
  const path = TAB_PATHS[tab];
  if (tab === "repository") return pathname.startsWith("/repository");
  if (tab === "profile") {
    return pathname === "/profile" || pathname.startsWith("/settings");
  }
  return pathname === path;
}

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDarkMode, theme } = useAppTheme();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--tab-bar-clearance",
      `${getTabBarTotalHeight()}px`
    );
    document.documentElement.style.removeProperty("--tab-rail-clearance");
    return () => {
      document.documentElement.style.removeProperty("--tab-bar-clearance");
    };
  }, []);

  const tabColors = useMemo(
    () =>
      isDarkMode
        ? {
            primary: theme.colors.primary,
            secondary: theme.colors.textSecondary,
            white: theme.colors.white,
            navbarBg: theme.colors.surfaceSecondary,
            profileRingBg: theme.colors.surfaceTertiary,
          }
        : LIGHT_TAB_COLORS,
    [isDarkMode, theme.colors]
  );

  const photoSrc = getProfilePhotoSrc(user ?? undefined);

  const iconColor = useCallback(
    (active: boolean) => (active ? tabColors.primary : tabColors.secondary),
    [tabColors]
  );

  const renderIcon = (tab: TabId, active: boolean) => {
    const color = iconColor(active);
    const variant = active ? "Bold" : "Outline";

    switch (tab) {
      case "home":
        return <Home2 size={SIDE_ICON_SIZE} color={color} variant={variant} />;
      case "favorites":
        return <Heart size={SIDE_ICON_SIZE} color={color} variant={variant} />;
      case "map":
        return (
          <Location size={SIDE_ICON_SIZE} color={color} variant={variant} />
        );
      case "profile":
        return (
          <ProfileAvatarRing $focused={active} $hasImage={Boolean(photoSrc)}>
            {photoSrc ? (
              <img src={photoSrc} alt="" />
            ) : (
              <User size={20} color={color} variant={variant} />
            )}
          </ProfileAvatarRing>
        );
      default:
        return null;
    }
  };

  const handleTabPress = (tab: TabId) => {
    navigate(TAB_PATHS[tab]);
  };

  const profileActive = isTabActive(location.pathname, "profile");

  return (
    <TabBarRoot aria-label="Navegação principal">
      <TabBarShadow $collapsed={collapsed}>
        <TabBarShell $collapsed={collapsed}>
          <MainRailGroup>
            {MAIN_TABS.map((tab) => {
              const active = isTabActive(location.pathname, tab);
              const hidden = collapsed;

              if (tab === "repository") {
                return (
                  <CenterSlot key={tab} $hidden={hidden}>
                    <CenterButton
                      type="button"
                      aria-label={TAB_LABELS.repository}
                      aria-current={active ? "page" : undefined}
                      onClick={() => handleTabPress(tab)}
                    >
                      <Folder
                        size={30}
                        color={tabColors.white}
                        variant="Bold"
                      />
                    </CenterButton>
                  </CenterSlot>
                );
              }

              return (
                <TabSlot key={tab} $hidden={hidden}>
                  <TabButton
                    type="button"
                    aria-label={TAB_LABELS[tab]}
                    aria-current={active ? "page" : undefined}
                    onClick={() => handleTabPress(tab)}
                    style={{
                      transform: active ? "scale(1.06)" : undefined,
                    }}
                  >
                    {renderIcon(tab, active)}
                  </TabButton>
                </TabSlot>
              );
            })}
          </MainRailGroup>

          <BottomRailGroup>
            <CollapseToggle
              type="button"
              aria-label={collapsed ? "Expandir barra" : "Recolher barra"}
              onClick={() => setCollapsed((v) => !v)}
            >
              {collapsed ? (
                <ArrowLeft2
                  size={16}
                  color={tabColors.secondary}
                  variant="Outline"
                />
              ) : (
                <ArrowRight2
                  size={16}
                  color={tabColors.secondary}
                  variant="Outline"
                />
              )}
            </CollapseToggle>
            <ProfileTabButton
              type="button"
              aria-label={TAB_LABELS.profile}
              aria-current={profileActive ? "page" : undefined}
              onClick={() => handleTabPress("profile")}
            >
              {renderIcon("profile", profileActive)}
            </ProfileTabButton>
          </BottomRailGroup>
        </TabBarShell>
      </TabBarShadow>
    </TabBarRoot>
  );
};

export default BottomNavigation;
